// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useParams,
} from '@tanstack/react-router';

// material ui
import { Box, Card, CardHeader, Stack, Tab, Tabs } from '@mui/material';

// api
import { useGetAccountByIdQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import { SuperuserOnly } from '@/components/access-control/AccessGuard';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// components
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

export const Route = createFileRoute('/$accountId/_user/account')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const location = useLocation();
  const { t } = useTranslation('account');

  // Determine current tab based on pathname
  const getCurrentTab = (): number => {
    if (location.pathname.includes('/profile')) return 0;
    if (location.pathname.includes('/users')) return 1;
    if (location.pathname.includes('/services')) return 2;
    return null;
  };

  const hideTabs = location.pathname.includes('/create'); // Hide tabs on create page

  const { accountId } = useParams({
    strict: false,
  });

  const { data: account } = useGetAccountByIdQuery(accountId);

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader title={t('title')} disableArrow />

      <Stack spacing={2}>
        <Card>
          <CardHeader
            title={account?.Name || t('accountHeader.notAvailable')}
            subheader={
              account?.Address || t('accountHeader.addressNotAvailable')
            }
            slotProps={{
              title: { variant: 'h6', fontWeight: 600 }
            }}
          />
        </Card>
        {!hideTabs && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', m: 2 }}>
            <Tabs
              value={getCurrentTab()}
              aria-label={t('tabs.aria_label')}
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  backgroundColor: 'brand.main',
                  borderRadius: '3px 3px 0 0',
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  transition: 'opacity 0.2s ease',
                },
              }}
            >
              <Tab
                label={t('tabs.details')}
                component={Link}
                to="/$accountId/account/profile"
                sx={{
                  textTransform: 'none',
                  ':hover': { color: 'primary.main' },
                }}
              />
              <Tab
                label={t('tabs.users')}
                component={Link}
                to="/$accountId/account/users"
                sx={{
                  textTransform: 'none',
                  ':hover': { color: 'primary.main' },
                }}
              />
              <SuperuserOnly>
                <Tab
                  label={t('tabs.services')}
                  component={Link}
                  to="/$accountId/account/services"
                  sx={{
                    textTransform: 'none',
                    ':hover': { color: 'primary.main' },
                  }}
                />
              </SuperuserOnly>
            </Tabs>
          </Box>
        )}

        <Outlet />
      </Stack>
    </PageContainer>
  );
}
